import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { resetPasswordWithToken, setPasswordWithToken } from "../../lib/api";
import form from "../../components/LoginForm.module.css";

export type PasswordTokenMode = "set" | "reset";

/**
 * Public landing page for emailed credential links (US-PIR-003): the token
 * arrives in the query string; the visitor sets a new password. One component
 * serves both the invite (set-password) and reset variants.
 */
export function PasswordTokenPage({ mode }: { mode: PasswordTokenMode }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await (mode === "set" ? setPasswordWithToken(token, password) : resetPasswordWithToken(token, password));
      setDone(true);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not set the password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <form className={form.root} onSubmit={(event) => void handleSubmit(event)} noValidate>
        <h1 className={form.title}>{mode === "set" ? "Set your password" : "Choose a new password"}</h1>
        {!token ? (
          <p className={form.error} role="alert">
            This link is missing its token. Ask for a new invite or reset link.
          </p>
        ) : done ? (
          <>
            <p className={form.error}>
              Password set. You can sign in with your new password.
            </p>
            <Link className={form.submit} to="/login">
              Go to sign in
            </Link>
          </>
        ) : (
          <>
            {error && (
              <p className={form.error} role="alert">
                {error}
              </p>
            )}
            <label className={form.field}>
              <span className={form.label}>New password</span>
              <input
                className={form.input}
                type="password"
                name="password"
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.currentTarget.value)}
                required
              />
            </label>
            <label className={form.field}>
              <span className={form.label}>Confirm password</span>
              <input
                className={form.input}
                type="password"
                name="confirmPassword"
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.currentTarget.value)}
                required
              />
            </label>
            <button type="submit" className={form.submit} disabled={submitting}>
              {submitting ? "Saving…" : "Save password"}
            </button>
          </>
        )}
      </form>
    </div>
  );
}
